// A moldura partilhada pelos jogos cronometrados (IDEA-031, IDEA-032).
//
// Os dois jogos têm exactamente o mesmo ciclo — uma de nós abre a ronda, cada
// uma joga o mesmo tabuleiro, e a ronda acaba quando as duas jogarem — por isso
// o cronómetro, o cartão da ronda e o painel de resultados vivem aqui. Cada jogo
// só traz o seu tabuleiro.
//
// O tempo da outra pessoa não vem da API antes de jogarmos, de propósito
// (ver services/timedGames.ts): saber "tens de bater 1:42" muda a forma como se
// joga, e não há maneira de desver.

import { useCallback, useEffect, useRef, useState } from "react";
import type { TimedGameId, TimedRoundView } from "@historia/shared/contracts";
import {
  hintPenaltyMs,
  revealPenaltyMs,
  timedResultTime,
} from "@historia/shared/validation";
import { useQueryClient } from "@tanstack/react-query";
import {
  AFTER_TIMED_GAME_CHANGE,
  createTimedRound,
  submitGameResult,
} from "@/api/mutations";
import { useAccao } from "@/api/useAccao";
import { useSession } from "@/api/session";
import { useTimedFinishedRounds } from "@/api/queries";
import { useOutroNome } from "@/components/jogos/useOutroNome";

/** "1:42" ou "12:07" a partir de milissegundos. */
export function formatarTempo(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const minutos = Math.floor(total / 60);
  const segundos = total % 60;
  return `${minutos}:${String(segundos).padStart(2, "0")}`;
}

/**
 * O cronómetro.
 *
 * Conta com `Date.now()` e não somando intervalos: um `setInterval` de 1s
 * atrasa-se sozinho, e no telemóvel o browser trava-o por completo com o ecrã
 * desligado. Guardar o instante do arranque e subtrair mantém o tempo certo
 * mesmo que o intervalo falhe metade dos disparos.
 */
export function useCronometro() {
  const [decorrido, setDecorrido] = useState(0);
  const inicio = useRef<number | null>(null);
  const [aContar, setAContar] = useState(false);

  useEffect(() => {
    if (!aContar) return;
    const id = window.setInterval(() => {
      if (inicio.current !== null) setDecorrido(Date.now() - inicio.current);
    }, 250);
    return () => window.clearInterval(id);
  }, [aContar]);

  const arrancar = useCallback(() => {
    if (inicio.current !== null) return; // já a contar — não reiniciar
    inicio.current = Date.now();
    setAContar(true);
  }, []);

  const parar = useCallback(() => {
    setAContar(false);
    return inicio.current === null ? 0 : Date.now() - inicio.current;
  }, []);

  /**
   * O tempo exacto, sem parar a contagem.
   *
   * `decorrido` só é actualizado de 250 em 250ms — é o que se mostra, e para
   * mostrar chega bem. Mas quem envia um resultado precisa do número certo: um
   * palpite dado antes do primeiro disparo do intervalo lia `decorrido` a 0, e
   * um 0 não é um tempo. O jogo dos emojis envia a cada palpite (só o certo é
   * guardado), por isso não pode usar `parar()` para medir.
   */
  const medir = useCallback(() => {
    return inicio.current === null ? 0 : Date.now() - inicio.current;
  }, []);

  const reiniciar = useCallback(() => {
    inicio.current = null;
    setDecorrido(0);
    setAContar(false);
  }, []);

  return { decorrido, aContar, arrancar, medir, parar, reiniciar };
}

/**
 * Como se descreve o resultado de uma pessoa, no jogo em que ela o fez.
 *
 * Os três jogos de tabuleiro medem-se em tempo; o Pinpoint mede-se em palpites e
 * não tem relógio nenhum. E "não terminou" quer dizer coisas diferentes: nos
 * primeiros é ter saído a meio, no Pinpoint é ter gasto os cinco palpites sem
 * acertar — que não é desistir, é perder.
 *
 * Uma coluna (`desistiu`) para a regra, que é a mesma; duas maneiras de a dizer,
 * porque quem lê não está a pensar em colunas.
 */
export function marcaDoResultado(
  r: { tempo_ms: number; pistas: number | null; palpites: number | null; desistiu: boolean },
  jogo: TimedGameId,
): string {
  if (jogo === "pinpoint") {
    if (r.desistiu) return "não acertou";
    const n = r.palpites ?? 0;
    return n === 1 ? "à primeira" : `${n} palpites`;
  }
  return r.desistiu ? "desistiu" : formatarTempo(timedResultTime(r, jogo));
}

/**
 * O mesmo resultado, mas dentro de uma frase: "acertaste à primeira",
 * "acertou em 3 palpites". Separado de `marcaDoResultado` porque um título
 * ("2 palpites") e uma frase ("em 2 palpites") não se escrevem da mesma maneira,
 * e colar um "em" à frente do título dava "acertaste em à primeira".
 */
function frasePalpites(n: number): string {
  return n === 1 ? "à primeira" : `em ${n} palpites`;
}

export function CartaoRondaCronometrada({
  jogo,
  ronda,
  aoJogar,
}: {
  jogo: TimedGameId;
  ronda: TimedRoundView | null;
  aoJogar: () => void;
}) {
  const [, abrir, aAbrir] = useAccao(createTimedRound, {
    invalidate: AFTER_TIMED_GAME_CHANGE,
  });
  const outro = useOutroNome();

  // A última ronda terminada, para a mostrar quando não há nenhuma a decorrer.
  // As três páginas já pedem esta consulta; o React Query devolve a mesma, por
  // isso isto não custa um pedido a mais.
  const terminadas = useTimedFinishedRounds(jogo);
  const ultima = terminadas.data?.[0] ?? null;

  if (!ronda) {
    return (
      <div className="flex flex-col gap-4">
        {/* Como acabou a última, antes de propor outra.
            Acabar o tabuleiro fecha a ronda, e fechar a ronda fá-la desaparecer
            do ecrã: quem carregava na última peça de um Shikaku de quatro
            minutos ficava a olhar para "não há nenhuma ronda a decorrer". Era o
            pior sítio possível para não haver nada. */}
        {ultima && <ResultadoRonda ronda={ultima} />}

        <div className="rounded-2xl border border-ouro/25 bg-creme-50 p-5 text-center">
          <p className="text-sm text-tinta-700">
            {ultima
              ? "Prontas para outra? O tabuleiro é sorteado na hora e fica igual para as duas."
              : "Não há nenhuma ronda a decorrer. Abre uma — o tabuleiro é sorteado na hora e fica igual para as duas."}
          </p>
          <button
            type="button"
            onClick={() => abrir(jogo)}
            disabled={aAbrir}
            className="mt-4 rounded-full bg-ouro px-6 py-2.5 text-sm font-semibold text-creme-50 shadow-[0_10px_28px_-10px_rgba(201,162,39,0.65)] transition-all hover:-translate-y-0.5 hover:bg-ouro-600 disabled:opacity-60"
          >
            {aAbrir ? "A abrir…" : "Abrir uma ronda"}
          </button>
        </div>
      </div>
    );
  }

  if (ronda.mine) {
    return (
      <div className="rounded-2xl border border-ouro/25 bg-creme-50 p-5 text-center">
        <p className="font-titulo text-2xl text-tinta-800">
          {ronda.mine.desistiu
            ? jogo === "pinpoint"
              ? "Não acertaste desta vez"
              : "Desististe desta ronda"
            : jogo === "pinpoint"
              ? `Acertaste ${frasePalpites(ronda.mine.palpites ?? 0)}`
              : `Fizeste ${marcaDoResultado(ronda.mine, jogo)}`}
        </p>
        <p className="mt-1 text-sm text-tinta-600">
          Falta {outro} jogar este tabuleiro. A ronda fecha quando as duas
          jogarem.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-ouro/25 bg-creme-50 p-5 text-center">
      <p className="text-sm text-tinta-700">
        {/* O relógio passou a arrancar no "Jogar" e esta linha ficou a dizer o
            contrário — prometia um tempo de estudo grátis que já não existe. */}
        Há uma ronda à tua espera.{" "}
        {jogo === "pinpoint"
          ? "Ganha quem precisar de menos palpites."
          : "O cronómetro arranca quando carregares em Jogar."}
      </p>
      <button
        type="button"
        onClick={aoJogar}
        className="mt-4 rounded-full bg-ouro px-6 py-2.5 text-sm font-semibold text-creme-50 shadow-[0_10px_28px_-10px_rgba(201,162,39,0.65)] transition-all hover:-translate-y-0.5 hover:bg-ouro-600"
      >
        Jogar
      </button>
    </div>
  );
}

/** O painel do fim: o teu tempo, o dela, e os pontos da ronda. */
export function ResultadoRonda({ ronda }: { ronda: TimedRoundView }) {
  const { user } = useSession();
  const outro = useOutroNome();
  const jogo = ronda.round.jogo;

  if (!ronda.mine) return null;

  const meus = ronda.mine;
  const outros = ronda.theirs;
  const meusPontos =
    ronda.pontos.find((p) => p.email === user?.email)?.points ?? null;

  // O tempo que conta é o do relógio mais as pistas, e é este que se mostra:
  // um painel a dizer 3:10 e a ronda a ser decidida por 3:30 seria mentira.
  const meuTempo = timedResultTime(meus, ronda.round.jogo);
  const outroTempo = outros ? timedResultTime(outros, ronda.round.jogo) : null;
  // As duas ajudas somam-se aqui na mesma conta, porque é uma conta só que a
  // linha por baixo tem de explicar: "3:10 + 60s de ajudas". Separá-las daria
  // duas parcelas para o mesmo relógio e ninguém lê isso.
  const penalizacao =
    (meus.pistas ?? 0) * hintPenaltyMs(ronda.round.jogo) +
    (meus.revelacoes ?? 0) * revealPenaltyMs(ronda.round.jogo);
  const ajudas = (meus.pistas ?? 0) + (meus.revelacoes ?? 0);

  // Uma desistência não é um tempo, é estar fora da corrida. Comparar relógios
  // dava a ronda a quem desistiu ao fim de dois segundos contra quem a jogou
  // toda — foi exactamente o que aconteceu antes desta linha existir.
  const ganhei = meus.desistiu
    ? false
    : outros?.desistiu
      ? true
      : outroTempo !== null && meuTempo < outroTempo;
  const empate =
    !meus.desistiu &&
    !outros?.desistiu &&
    outroTempo !== null &&
    meuTempo === outroTempo;

  return (
    <div className="rounded-2xl border border-ouro/30 bg-creme-50 p-5 text-center">
      <p className="font-titulo text-3xl text-tinta-800">
        {meus.desistiu
          ? jogo === "pinpoint"
            ? "Não acertaste"
            : "Desististe"
          : marcaDoResultado(meus, jogo)}
      </p>
      <p className="mt-1 text-sm text-tinta-600">
        {meus.movimentos !== null && `${meus.movimentos} jogadas`}
        {penalizacao > 0 &&
          `${formatarTempo(meus.tempo_ms)} + ${Math.round(penalizacao / 1000)}s de ${ajudas} ${ajudas === 1 ? "ajuda" : "ajudas"}`}
        {meus.erros
          ? ` · ${meus.erros} ${meus.erros === 1 ? "erro" : "erros"}`
          : ""}
      </p>

      {outros ? (
        <>
          <p className="mt-4 text-sm text-tinta-700">
            {outros.desistiu ? (
              jogo === "pinpoint" ? (
                <>{outro} não acertou.</>
              ) : (
                <>{outro} desistiu desta ronda.</>
              )
            ) : (
              <>
                {outro} {jogo === "pinpoint" ? "acertou" : "fez"}{" "}
                <strong>
                  {jogo === "pinpoint"
                    ? frasePalpites(outros.palpites ?? 0)
                    : marcaDoResultado(outros, jogo)}
                </strong>.
              </>
            )}
          </p>
          <p className="mt-2 font-titulo text-xl text-ouro-600">
            {empate
              ? "Empate — ao segundo!"
              : ganhei
                ? "Ganhaste esta ronda! 💛"
                : meus.desistiu
                  ? jogo === "pinpoint"
                    ? `Não acertaste — a ronda foi de ${outro}.`
                    : "Desististe — a ronda foi dela."
                  : `Desta vez foi de ${outro}.`}
          </p>
        </>
      ) : (
        <p className="mt-4 text-sm text-tinta-600">
          Agora é esperar. O tempo de {outro} aparece aqui quando jogar.
        </p>
      )}

      {/* Só quando há mesmo ponto: "+0 pontos para o placar" não é informação,
          é sal na ferida. Quem perdeu já leu que perdeu, na linha de cima. */}
      {meusPontos !== null && meusPontos > 0 && (
        <p className="mt-3 text-sm font-semibold text-tinta-700">
          +{meusPontos} {meusPontos === 1 ? "ponto" : "pontos"} para o placar
        </p>
      )}
    </div>
  );
}

/**
 * Envia o tempo e devolve o estado da ronda.
 *
 * Fica aqui porque os dois jogos precisam exactamente do mesmo: enviar uma vez
 * só, invalidar o placar e a página inicial, e nunca reenviar (a API responde
 * 409, mas o melhor é nem chegar lá).
 */
export function useEnviarResultado(jogo: TimedGameId) {
  const queryClient = useQueryClient();
  const enviado = useRef(false);

  const [estado, enviar, aEnviar] = useAccao(submitGameResult, {
    invalidate: AFTER_TIMED_GAME_CHANGE,
  });

  const submeter = useCallback(
    async (
      roundId: string,
      dados: {
        tempoMs: number;
        movimentos?: number;
        pistas?: number;
        revelacoes?: number;
        erros?: number;
        palpites?: number;
        desistiu?: boolean;
      },
    ) => {
      if (enviado.current) return;
      enviado.current = true;
      await enviar({ jogo, roundId, ...dados });
      void queryClient.invalidateQueries({ queryKey: ["games"] });
    },
    [enviar, jogo, queryClient],
  );

  return { estado, submeter, aEnviar };
}
